import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AlertController, IonicModule, IonModal, ToastController } from '@ionic/angular';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { BerService } from '../ber.service';
import { honapNev } from '../ber.opciok';
import { BerJuttatasApi, csvLetolt, ftSzoveg, HONAPOK_EVVEL, szazalek, tizedes } from '../juttatas/juttatas.api';
import { JuttatasParamComponent } from '../juttatas/juttatas-param.component';
import { SzerkZar, SzerkZarService } from '../../components/szerk-zar/szerk-zar.service';
import { SzerkZarSavComponent } from '../../components/szerk-zar/szerk-zar-sav.component';

/**
 *  ADATOK RÖGZÍTÉSE (EFO) – egyszerűsített foglalkoztatás (RLB: Számfejtés → Adatok rögzítése (EFO)).
 *
 *  Egy sor = egy munkavállaló egy napja: alkalmi munka, mezőgazdasági vagy turisztikai idénymunka,
 *  filmipari statiszta. A munkavállaló a törzsből választható, vagy – alkalmi munkásnál – névvel,
 *  adóazonosító jellel és TAJ-számmal adható meg. A kiszolgáló számolja a napi közterhet (2026-tól
 *  a minimálbér %-a, száz forintra kerekítve; a mezőgazdasági idénymunka 121. napjától magasabb),
 *  és figyelmeztet a korlátokra (5 egymást követő nap, havi 15 nap, éves 120 / 210 nap,
 *  létszámkeret, legkisebb bér, a statiszta kereseti korlátja). A figyelmeztetés nem tilt.
 *
 *  A NAV-bejelentést a munkavégzés megkezdése ELŐTT kell megtenni: innen a T1042E adatlap a
 *  nyomtatványkitöltőben nyílik meg (ellenőrzés, gépi beküldés), mellette a lista és a CSV.
 */
@Component({
  selector: 'app-ber-efo',
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule, JuttatasParamComponent, SzerkZarSavComponent],
  templateUrl: './efo.component.html',
  styleUrls: ['../../naplo/naplo-shared.scss', '../ber-shared.scss', './efo.component.scss'],
})
export class EfoComponent implements OnInit, OnDestroy {
  @ViewChild('rogModal') rogModal: IonModal;
  @ViewChild('napModal') napModal: IonModal;
  @ViewChild('paramModal') paramModal: IonModal;
  evek: number[] = [];
  honapok = HONAPOK_EVVEL;
  honapNev = honapNev;
  ft = ftSzoveg;
  szazalek = szazalek;
  tizedes = tizedes;

  ho = 0;
  d: any = null;               // a /efo válasza
  param: any = {};
  alkalmazottak: any[] = [];
  letszam = { letszam_1: null as number | null, letszam_2: null as number | null };
  rog: any = null;             // a rögzítő ablak modellje
  nap: any = null;             // egy nap módosítása
  mentes = false;

  constructor(private ber: BerService, private api: BerJuttatasApi, private toast: ToastController, private alert: AlertController,
              private router: Router, private http: HttpClient, private szerkZar: SzerkZarService) {}

  /*  BEJELENTÉS A NAV-HOZ (T1042E) – innen indítva.
   *  A rögzített napokból a kiszolgáló összeállítja a bejelentőlapot (személyenként és
   *  munkajellegenként az egymást követő napokat egy tételbe vonva), majd a nyomtatványkitöltőben
   *  ellenőrizhető és beküldhető – abba a környezetbe, amelyik a NAV-kapcsolatnál használatban van.
   *  A bejelentést a munkavégzés MEGKEZDÉSE ELŐTT kell beküldeni. */
  bejelentFut = false;
  async bejelentes() {
    if (!this.aktiv.ugyfelId) return;
    const ma = new Date().toISOString().slice(0, 10);
    const a = await this.alert.create({
      header: 'EFO-bejelentés (T1042E)',
      message: 'Mely napokat vigyük a bejelentőlapra? A kitöltőben minden sor ellenőrizhető és javítható, '
        + 'a beküldés külön lépés.',
      inputs: [
        { type: 'radio', label: `A megjelenített hónap napjai (${this.honapNev(this.ho)} ${this.aktiv.ev})`, value: 'honap', checked: true },
        { type: 'radio', label: 'Egy adott nap', value: 'nap' },
      ],
      buttons: [{ text: 'Mégse', role: 'cancel' }, { text: 'Tovább', role: 'ok' }],
    });
    await a.present();
    const v = await a.onDidDismiss();
    if (v.role !== 'ok') return;
    let datum: string | null = null;
    if (v.data?.values === 'nap') {
      const n = await this.alert.create({
        header: 'Melyik nap?', inputs: [{ type: 'date', name: 'datum', value: ma }],
        buttons: [{ text: 'Mégse', role: 'cancel' }, { text: 'Tovább', role: 'ok' }],
      });
      await n.present();
      const nv = await n.onDidDismiss();
      if (nv.role !== 'ok' || !nv.data?.values?.datum) return;
      datum = String(nv.data.values.datum).slice(0, 10);
    }
    this.bejelentFut = true;
    try {
      const ny = <T = any>(ut: string, test?: any): Promise<T> => firstValueFrom(test === undefined
        ? this.http.get<T>('/api/nav-nyomtatvany' + ut) : this.http.post<T>('/api/nav-nyomtatvany' + ut, test));
      const katalogus: any = await ny('/katalogus');
      if (!(katalogus.lista || []).some((k: any) => k.kod === 'T1042E' && k.letoltve)) await ny('/sablon/T1042E/letolt', {});
      const ho = datum ? Number(datum.slice(5, 7)) : (this.ho || new Date().getMonth() + 1);
      const r: any = await ny('/bevallas', {
        modul: 'BER', ugyfel_id: this.aktiv.ugyfelId, kod: 'T1042E', ev: datum ? Number(datum.slice(0, 4)) : this.aktiv.ev,
        gyakorisag: 'havi', ho, kitoltesParam: datum ? { datum } : {},
      });
      const k = r.kitoltes || {};
      if (!k.tetelek) this.figyelmeztet('A választott időszakra nincs rögzített EFO-nap – üres bejelentőlap nyílt meg.');
      else this.ok(`${k.tetelek} tétel került a bejelentőlapra.`);
      this.router.navigate(['/ber/bevallas', r.id]);
    } catch (e: any) { this.hiba(e?.error?.error || 'Nem sikerült előkészíteni a bejelentést.'); }
    finally { this.bejelentFut = false; }
  }

  /*  A NAV-NÁL NYILVÁNTARTOTT EFO-NAPOK (M2M).
   *  A NAV gépi úton megmondja, hogy a dolgozó a tárgyévben hány EFO-napot használt már fel és
   *  mennyi maradt, munkajellegenként. Ez a más munkáltatónál ledolgozott napokat is tartalmazza –
   *  amit mi magunktól nem tudhatunk, viszont a korlátba beleszámít. A lekérdezés abba a
   *  környezetbe megy (teszt/éles), amelyik a Szervíz → NAV-kapcsolat képernyőn használatban van. */
  navSorok: any[] | null = null;
  navKornyezet: string | null = null;
  navFut = false;
  navJelleg = [
    { k: 'alkalmi', cim: 'Alkalmi' }, { k: 'mezogazdasag', cim: 'Mezőgazdasági' },
    { k: 'turisztika', cim: 'Turisztikai' }, { k: 'filmipar', cim: 'Filmipari' },
  ];
  async navLista() {
    if (!this.aktiv.ugyfelId) return;
    this.navFut = true;
    try {
      const r: any = await this.api.post(`/efo/${this.aktiv.ugyfelId}/nav-lista?ev=${this.aktiv.ev}`, {});
      this.navSorok = r.sorok || [];
      this.navKornyezet = r.kornyezet;
      if (!this.navSorok.length) this.figyelmeztet(r.uzenet || 'A NAV nem adott vissza EFO-adatot erre az évre.');
    } catch (e: any) { this.hiba(e?.error?.error || 'A NAV-lekérdezés nem sikerült.'); }
    finally { this.navFut = false; }
  }
  /** Egy munkavállaló napjai a NAV-nál (a rögzítő ablakból, mielőtt napot írnánk rá). */
  async navSzemely(sor: any) {
    this.navFut = true;
    try {
      const r: any = await this.api.post(`/efo/${this.aktiv.ugyfelId}/nav-szemely`, {
        alkalmazott_id: sor?.alkalmazott_id || null, adoazonosito: sor?.adoazonosito, nev: sor?.nev, taj: sor?.taj,
      });
      const f = r.felhasznalhato || {};
      this.ok(`${r.nev} (NAV, ${r.kornyezet}): még felhasználható – alkalmi ${f.alkalmi}, mezőgazdasági ${f.mezogazdasag}, `
        + `turisztikai ${f.turisztika}, filmipari ${f.filmipar} nap.`);
      for (const h of r.hibak || []) this.figyelmeztet('NAV: ' + h);
    } catch (e: any) { this.hiba(e?.error?.error || 'A NAV-lekérdezés nem sikerült.'); }
    finally { this.navFut = false; }
  }

  async ngOnInit() {
    await this.ber.init();
    const ev = new Date().getFullYear();
    this.evek = [ev + 1, ev, ev - 1, ev - 2, ev - 3];
    this.ho = this.aktiv.ho;
    await this.betolt();
  }

  get aktiv() { return this.ber.aktiv; }
  async evValt(ev: number) { await this.ber.setAktiv({ ev }); await this.betolt(); }
  async hoValt(ho: number) { this.ho = ho; if (ho) await this.ber.setAktiv({ ho }); await this.betolt(); }
  get idoszak(): string { return this.ho ? `${this.aktiv.ev}. ${honapNev(this.ho).toLowerCase()}` : `${this.aktiv.ev}. év`; }

  async betolt() {
    if (!this.aktiv.ugyfelId) return;
    try {
      const u = this.aktiv.ugyfelId;
      const [d, p, alk]: any[] = await Promise.all([
        this.api.get(`/efo/${u}?ev=${this.aktiv.ev}&ho=${this.ho}`),
        this.api.get(`/param/${u}?ev=${this.aktiv.ev}`),
        this.ber.get('/alkalmazottak/' + u).catch(() => []),
      ]);
      this.d = d;
      this.param = Object.fromEntries((p.parameterek || []).map((x: any) => [x.kulcs, x.ertek]));
      this.alkalmazottak = (alk || []).filter((a: any) => !a.inaktiv);
      this.letszam = { letszam_1: d.letszam.letszam_1, letszam_2: d.letszam.letszam_2 };
    } catch (e: any) { this.hiba(e?.error?.error || 'Nem sikerült betölteni.'); }
  }

  tipusCim(t: string): string { return this.d?.tipusok?.find((x: any) => x.tipus === t)?.cim || t; }
  tipusRovid(t: string): string { return this.d?.tipusok?.find((x: any) => x.tipus === t)?.rovid || t; }
  tipusSzoveg(tipusok: Record<string, number>): string {
    return Object.entries(tipusok || {}).map(([t, n]) => `${this.tipusRovid(t)}: ${n}`).join(' · ');
  }
  get hibak(): any[] { return (this.d?.figyelmeztetesek || []).filter((f: any) => f.szint === 'hiba'); }
  szintIkon(sz: string): string { return sz === 'hiba' ? 'alert-circle' : sz === 'figyelem' ? 'warning-outline' : 'information-circle-outline'; }
  /** A közteher esedékessége: a tárgyhónapot követő hónap 12-e (NAV 46. füzet 3. pont). */
  esedekesHo(h: number): string {
    const ev = h === 12 ? this.aktiv.ev + 1 : this.aktiv.ev; const ho = h === 12 ? 1 : h + 1;
    return `${ev}. ${String(ho).padStart(2, '0')}. 12.`;
  }
  get esedekes(): string { return this.ho ? this.esedekesHo(this.ho) : 'havonta, a következő hónap 12-éig'; }

  /* ── napok rögzítése ── */
  /** A korábban (az évben) már rögzített, törzsön kívüli munkavállalók a gyors kitöltéshez. */
  get korabbiKulsok(): any[] {
    const lat = new Map<string, any>();
    for (const s of (this.d?.sorok || [])) if (!s.alkalmazott_id && s.adoazonosito) lat.set(s.adoazonosito, s);
    return [...lat.values()].sort((a, b) => String(a.nev).localeCompare(String(b.nev), 'hu'));
  }
  uj() {
    const ho = this.ho || this.aktiv.ho;
    const ma = new Date();
    const nap = ma.getFullYear() === this.aktiv.ev && ma.getMonth() + 1 === ho ? ma.getDate() : 1;
    const datum = `${this.aktiv.ev}-${String(ho).padStart(2, '0')}-${String(nap).padStart(2, '0')}`;
    this.rog = { forras: this.alkalmazottak.length ? 'torzs' : 'kulso', alkalmazott_id: null, nev: '', adoazonosito: '', taj: '',
      tipus: 'alkalmi', tol: datum, ig: datum, hetvege_nelkul: false, napi_ber: null, orak: 8, szakkepzett: false, megjegyzes: '', korabbi: null };
    this.rogModal.present();
  }
  korabbiValaszt(adoazonosito: string) {
    const k = this.korabbiKulsok.find((x) => x.adoazonosito === adoazonosito);
    if (k) Object.assign(this.rog, { nev: k.nev, adoazonosito: k.adoazonosito, taj: k.taj || '', tipus: k.tipus, napi_ber: Number(k.napi_ber) || null,
      orak: Number(k.orak) || 8, szakkepzett: !!Number(k.szakkepzett) });
  }
  /** A kijelölt időszak napjai (a hétvégék nélkül, ha úgy kérték). */
  napokSzama(r: any): number {
    if (!r?.tol) return 0;
    const ig = r.ig && r.ig >= r.tol ? r.ig : r.tol;
    let n = 0;
    for (let t = Date.parse(r.tol + 'T00:00:00Z'); t <= Date.parse(ig + 'T00:00:00Z'); t += 86400000) {
      const hn = new Date(t).getUTCDay();
      if (r.hetvege_nelkul && (hn === 0 || hn === 6)) continue;
      n++;
    }
    return n;
  }
  napiKozteher(tipus: string): number { return Number(this.d?.napi_kozteher?.[tipus]) || 0; }
  /** A legkisebb napi bér (Efo tv. 4. § (1a)) – a kiszolgálóéval azonos képlet. */
  legkisebbBer(r: any): number {
    const havi = r?.szakkepzett ? (this.param.garantalt_berminimum || 0) * (this.param.efo_ber_gar_hanyad || 0)
      : (this.param.minimalber || 0) * (this.param.efo_ber_min_hanyad || 0);
    return Math.ceil(havi / (this.param.efo_havi_oraszam || 174) * (Number(r?.orak) || 8));
  }

  async rogzit() {
    const r = this.rog;
    if (r.forras === 'torzs' && !r.alkalmazott_id) { this.hiba('Válassz munkavállalót a törzsből.'); return; }
    if (r.forras === 'kulso' && (!r.nev?.trim() || !r.adoazonosito?.trim())) { this.hiba('A név és az adóazonosító jel kötelező.'); return; }
    if (!(Number(r.napi_ber) > 0)) { this.hiba('A napi bér kötelező.'); return; }
    const b: any = { tipus: r.tipus, tol: r.tol, ig: r.ig || r.tol, hetvege_nelkul: !!r.hetvege_nelkul, napi_ber: Number(r.napi_ber),
      orak: Number(r.orak) || 8, szakkepzett: !!r.szakkepzett, megjegyzes: r.megjegyzes || null };
    if (r.forras === 'torzs') b.alkalmazott_id = r.alkalmazott_id;
    else Object.assign(b, { nev: r.nev.trim(), adoazonosito: r.adoazonosito.trim(), taj: (r.taj || '').trim() || null });
    this.mentes = true;
    try {
      const v: any = await this.api.post(`/efo/${this.aktiv.ugyfelId}`, b);
      await this.rogModal.dismiss();
      this.ok(`${v.letrehozva} nap rögzítve${v.kihagyva?.length ? ` (${v.kihagyva.length} már rögzített nap kimaradt)` : ''}.`);
      await this.betolt();
    } catch (e: any) { this.hiba(e?.error?.error || 'Mentési hiba.'); }
    finally { this.mentes = false; }
  }

  /* ── egy nap módosítása / törlése ── */
  modosit(s: any) {
    this.nap = this.napSzerkbol(s);
    this.napModal.present();
    this.zarFoglal(s);
  }
  private napSzerkbol(s: any) {
    return { id: s.id, nev: s.nev, datum: s.datum, tipus: s.tipus, napi_ber: Number(s.napi_ber), orak: Number(s.orak) || 8,
      szakkepzett: !!Number(s.szakkepzett), megjegyzes: s.megjegyzes || '' };
  }

  /*  SZERKESZTÉSI ZÁR (components/szerk-zar) a módosított EFO-napra: egyszerre egy ember szerkesztheti.
   *  A sorszám a késve érkező foglalást dobja el, ha a szerkesztőt közben bezárták. */
  zarNap: SzerkZar | null = null;
  private zarSorsz = 0;
  private async zarFoglal(s: any) {
    const n = ++this.zarSorsz;
    this.zarNap?.felenged(); this.zarNap = null;
    const z = await this.szerkZar.foglal('ber', 'ber_efo', s.id, { ujratolt: () => this.napUjratolt(s) });
    if (n !== this.zarSorsz) { z?.felenged(); return; }
    this.zarNap = z;
  }
  zarEngedi() { this.zarSorsz++; this.zarNap?.felenged(); this.zarNap = null; }
  /** Friss adat a nyitott szerkesztőbe (a nap hónapjának soraiból). */
  private async napUjratolt(s: any) {
    const d = String(s.datum || '');
    const ev = Number(d.slice(0, 4)) || this.aktiv.ev; const ho = Number(d.slice(5, 7)) || this.ho;
    const v: any = await this.api.get(`/efo/${this.aktiv.ugyfelId}?ev=${ev}&ho=${ho}`);
    const friss = (v?.sorok || []).find((x: any) => x.id === s.id);
    if (this.nap?.id !== s.id) return;
    if (friss) this.nap = this.napSzerkbol(friss);
    else { this.hiba('Ezt a napot közben törölték.'); await this.napModal.dismiss(); }
  }
  ngOnDestroy() { this.zarEngedi(); }

  async napMent() {
    const n = this.nap;
    if (this.zarNap && !this.zarNap.irhato) return;
    this.mentes = true;
    try {
      await this.api.put(`/efo/sor/${n.id}`, { datum: n.datum, tipus: n.tipus, napi_ber: Number(n.napi_ber), orak: Number(n.orak) || 8,
        szakkepzett: !!n.szakkepzett, megjegyzes: n.megjegyzes || null });
      await this.napModal.dismiss();
      this.ok('Mentve.');
      await this.betolt();
    } catch (e: any) { this.hiba(e?.error?.error || 'Mentési hiba.'); }
    finally { this.mentes = false; }
  }
  async torol(s: any) {
    const a = await this.alert.create({
      header: 'EFO-nap törlése', message: `${s.nev} · ${s.datum} · ${this.tipusCim(s.tipus)}`,
      buttons: [{ text: 'Mégse', role: 'cancel' }, { text: 'Törlés', role: 'destructive', handler: async () => {
        try { await this.api.del(`/efo/sor/${s.id}`); this.ok('Törölve.'); await this.betolt(); }
        catch (e: any) { this.hiba(e?.error?.error || 'A törlés nem sikerült.'); }
      } }],
    });
    await a.present();
  }

  async letszamMent() {
    try {
      const v = (x: any) => (x === null || x === '' || x === undefined ? null : Number(x));
      await this.api.put(`/efo/letszam/${this.aktiv.ugyfelId}?ev=${this.aktiv.ev}`, { letszam_1: v(this.letszam.letszam_1), letszam_2: v(this.letszam.letszam_2) });
      this.ok('Létszám mentve.');
      await this.betolt();
    } catch (e: any) { this.hiba(e?.error?.error || 'Mentési hiba.'); }
  }

  /* ── nyomtatás, CSV ── */
  async nyomtatOsszesito() {
    const sorok = this.d?.dolgozok || [];
    const oszlopok = [
      { k: 'nev', c: 'Munkavállaló', szeles: true }, { k: 'adoazonosito', c: 'Adóazonosító' }, { k: 'taj', c: 'TAJ' },
      { k: 'tipusok', c: 'Jelleg (napok)', szeles: true }, { k: 'napok', c: 'Napok', num: true },
      { k: 'ber', c: 'Bér összesen', num: true }, { k: 'kozteher', c: 'Közteher', num: true }, { k: 'ev_napok', c: 'Éves napok', num: true },
    ];
    const o = this.d?.osszesen || {};
    await this.api.nyomtat('Egyszerűsített foglalkoztatás – összesítő', [['Időszak', this.idoszak], ['Közteher esedékes', this.esedekes]], oszlopok, sorok,
      (s, c) => (c.k === 'tipusok' ? this.tipusSzoveg(s.tipusok) : c.k === 'napok' || c.k === 'ev_napok' ? String(s[c.k] || 0) : c.num ? ftSzoveg(s[c.k]) : String(s[c.k] ?? '')),
      (c) => (c.k === 'napok' ? String(o.napok || 0) : c.k === 'ber' ? ftSzoveg(o.ber) : c.k === 'kozteher' ? ftSzoveg(o.kozteher) : ''));
  }
  async nyomtatNapok() {
    const sorok = this.d?.sorok || [];
    const oszlopok = [
      { k: 'datum', c: 'Dátum' }, { k: 'nev', c: 'Munkavállaló', szeles: true }, { k: 'adoazonosito', c: 'Adóazonosító' },
      { k: 'tipus', c: 'Jelleg' }, { k: 'orak', c: 'Óra', num: true }, { k: 'napi_ber', c: 'Napi bér', num: true },
      { k: 'kozteher', c: 'Közteher', num: true }, { k: 'megjegyzes', c: 'Megjegyzés', szeles: true },
    ];
    const o = this.d?.osszesen || {};
    await this.api.nyomtat('Egyszerűsített foglalkoztatás – napok', [['Időszak', this.idoszak]], oszlopok, sorok,
      (s, c) => (c.k === 'tipus' ? this.tipusCim(s.tipus) : c.k === 'orak' ? String(Number(s.orak) || '') : c.num ? ftSzoveg(s[c.k]) : String(s[c.k] ?? '')),
      (c) => (c.k === 'napi_ber' ? ftSzoveg(o.ber) : c.k === 'kozteher' ? ftSzoveg(o.kozteher) : ''));
  }
  /** A NAV-bejelentéshez szükséges adatok (Efo tv. 11. §; NAV 46. füzet 4.1): időszakonként egy sor. */
  csvBejelentes() {
    const adoszam = this.d?.ugyfel?.adoszam || '';
    csvLetolt(`efo_bejelentes_${this.aktiv.ev}${this.ho ? '_' + String(this.ho).padStart(2, '0') : ''}.csv`, [
      ['Munkáltató adószáma', 'Munkavállaló neve', 'Adóazonosító jel', 'TAJ-szám', 'Foglalkoztatás jellege', 'Első nap', 'Utolsó nap', 'Napok száma', 'Napi bér (Ft)', 'Bér összesen (Ft)'],
      ...(this.d?.bejelentesek || []).map((b: any) => [adoszam, b.nev, b.adoazonosito || '', b.taj || '', b.tipus_cim, b.kezdo, b.zaro, b.napok,
        b.napi_ber_min === b.napi_ber_max ? Math.round(b.napi_ber_min) : `${Math.round(b.napi_ber_min)}–${Math.round(b.napi_ber_max)}`, Math.round(b.ber_osszesen)]),
    ]);
  }

  async parameterek() { await this.paramModal.present(); }
  async paramMentve() { await this.paramModal.dismiss(); await this.betolt(); }

  private async figyelmeztet(m: string) { (await this.toast.create({ message: m, duration: 4200, color: 'warning', position: 'top' })).present(); }
  private async ok(m: string) { (await this.toast.create({ message: m, duration: 4000, color: 'success' })).present(); }
  private async hiba(m: string) { (await this.toast.create({ message: m, duration: 3000, color: 'danger' })).present(); }
}
